import { isBadSatisfactionRating, RatingType, type SatisfactionForm } from "@do/shared-types";
import { Button } from "@do/ui/components/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@do/ui/components/dialog";
import { Field, FieldError } from "@do/ui/components/field";
import { Label } from "@do/ui/components/label";
import { Textarea } from "@do/ui/components/textarea";
import { cn } from "@do/ui/lib/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { StarIcon, XIcon } from "lucide-react";
import { AnimatePresence, motion, useIsPresent } from "motion/react";
import { useLocale, useTranslations } from "next-intl";
import { type ReactNode, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useDebouncedCallback } from "use-debounce";
import { z } from "zod";
import { useMotionEnabled } from "../../../../hooks/use-motion-enabled";
import {
  getRevealProps,
  MESSAGE_REVEAL_OFFSET_Y,
  MESSAGE_REVEAL_TIMING,
} from "../../../../lib/motion-presets";
import { useChat } from "../../../../providers/chat-provider";
import { useSatisfactionFormVisibility } from "./use-satisfaction-form-visibility";

// Coalesce rapid rating changes (e.g. user clicks 3 then 5) into a single submission.
const RATING_SUBMIT_DEBOUNCE_MS = 600;

const FEEDBACK_MAX_LENGTH = 2048;

/**
 * Holds the card while it leaves, and keeps it out of reach while it does.
 *
 * A card on its way out is still in the document, still clickable and still in the tab
 * order: a rating picked there would be submitted after its debounce with nothing on
 * screen to show for it. `inert` makes the exit purely visual.
 *
 * The card leaves by fading and sliding only. Folding its height away would close the gap
 * it leaves more smoothly, but resizing a bordered, padded box mid-flight looks worse than
 * the single reflow it saves.
 */
const ReviewPresence = ({ children }: { children: ReactNode }) => {
  const isPresent = useIsPresent();
  const ref = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (isPresent) {
      return;
    }

    const node = ref.current;
    const active = document.activeElement;

    if (node && active instanceof HTMLElement && node.contains(active)) {
      active.blur();
    }
  }, [isPresent]);

  return (
    <div inert={!isPresent || undefined} ref={ref}>
      {children}
    </div>
  );
};

type FeedbackFormValues = { feedbackText: string };

// Emojis are universal and not translated; only their labels are localized.
const SMILEY_EMOJIS: Record<number, string> = {
  1: "😠",
  2: "🙁",
  3: "😐",
  4: "😃",
  5: "🤩",
};

const FIVE_POINT_VALUES = [1, 2, 3, 4, 5];
const NUMBER_SCALE_VALUES = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

type MessageReviewProps = {
  satisfactionForm?: SatisfactionForm | null;
};

export const MessageReview = ({ satisfactionForm }: MessageReviewProps) => {
  const t = useTranslations("do-chat-v2");
  const locale = useLocale();
  const motionEnabled = useMotionEnabled();
  const {
    messages,
    lastClickedButton,
    isLoading,
    scrollToBottom,
    isPreviewMode,
    dialogPortalContainer,
    onSubmitSurvey,
    onBadRating,
    onTrackEvent,
    recordedEventTypes,
    liveChatEndedAt,
    isLiveChatActive,
  } = useChat();

  const { isVisible, dismiss } = useSatisfactionFormVisibility({
    satisfactionForm,
    messages,
    lastClickedButton,
    isLoading,
    forceVisible: isPreviewMode,
    recordedEventTypes,
    onTrackEvent,
    liveChatEndedAt,
    isLiveChatActive,
  });

  // Scroll into view once the form appears (after layout so scrollHeight includes it).
  useEffect(() => {
    if (!isVisible) return;
    requestAnimationFrame(() => {
      scrollToBottom({ force: true, smooth: true });
    });
  }, [isVisible, scrollToBottom]);

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isReviewTextSent, setIsReviewTextSent] = useState(false);
  const [selectedReview, setSelectedReview] = useState<number | undefined>(undefined);
  const [hoveredReview, setHoveredReview] = useState<number | undefined>(undefined);

  // Built here (not at module level) so the validation message is localized in the
  // current bot language.
  const feedbackFormSchema = useMemo(
    () =>
      z.object({
        feedbackText: z
          .string()
          .trim()
          .min(1, t("chat.message-review.feedback-required"))
          .max(
            FEEDBACK_MAX_LENGTH,
            t("chat.message-review.feedback-too-long", {
              max: FEEDBACK_MAX_LENGTH,
            })
          ),
      }),
    [t]
  );

  const {
    control,
    handleSubmit: handleFeedbackFormSubmit,
    clearErrors,
    reset,
  } = useForm<FeedbackFormValues>({
    resolver: zodResolver(feedbackFormSchema),
    defaultValues: { feedbackText: "" },
    mode: "onChange",
  });

  const handleDialogOpenChange = (open: boolean) => {
    if (!open) {
      // Dismiss validation errors when closing, but keep the draft text for next time.
      clearErrors("feedbackText");
    }
    setIsDialogOpen(open);
  };

  // Set once the host has been asked to redirect, so a user who rates badly, changes
  // their mind and rates badly again is only sent on once (DAS-2868).
  const hasAskedForRedirectionRef = useRef(false);

  // A bad rating hands the conversation back to the bot, which answers with the standard
  // "bad feedback" node, so the survey is not a dead end for someone who just told us the
  // bot did not help (DAS-2868). Called from wherever the rating is submitted; both paths
  // are already out of reach in preview mode, where there is no conversation to send on.
  const redirectOnBadRating = (ratingValue: number) => {
    if (hasAskedForRedirectionRef.current) return;
    if (!satisfactionForm?.redirection_bad_rating) return;
    const rating = satisfactionForm.rating_type;
    if (!rating || !isBadSatisfactionRating(rating, ratingValue)) return;
    // An advisor is answering: the command would reach the bot, not them.
    if (isLiveChatActive) return;
    hasAskedForRedirectionRef.current = true;
    void onBadRating?.();
  };

  // Persist the rating, but debounce so changing one's mind (3 -> 5) sends only the
  // last value instead of one request per click.
  const debouncedSubmitRating = useDebouncedCallback((value: number) => {
    const rating = satisfactionForm?.rating_type;
    if (!rating) return;
    void onSubmitSurvey?.({ ratingType: rating, ratingValue: value });
    redirectOnBadRating(value);
  }, RATING_SUBMIT_DEBOUNCE_MS);

  // Flush any pending rating submit if the form unmounts (e.g. dismissed) before the
  // debounce fires, so a last-second rating isn't lost.
  useEffect(() => () => debouncedSubmitRating.flush(), [debouncedSubmitRating]);

  const showFollowUp =
    selectedReview !== undefined &&
    satisfactionForm?.enable_text_feedback === true &&
    !isReviewTextSent;

  // Final confirmation shown once the user has submitted their written feedback.
  const showFeedbackThankYou = isReviewTextSent;

  // Scroll into view when the post-rating follow-up (thank you + feedback button) appears.
  useEffect(() => {
    if (!showFollowUp) return;
    requestAnimationFrame(() => {
      scrollToBottom({ force: true, smooth: true });
    });
  }, [showFollowUp, scrollToBottom]);

  // Scroll into view when the post-submission thank-you message appears.
  useEffect(() => {
    if (!showFeedbackThankYou) return;
    requestAnimationFrame(() => {
      scrollToBottom({ force: true, smooth: true });
    });
  }, [showFeedbackThankYou, scrollToBottom]);

  // Pick the language variant matching the current chat locale (keys look like "fr-fr"),
  // falling back to the first available variant.
  const variant = useMemo(() => {
    const variants = satisfactionForm?.language_variants;
    if (!variants) return null;
    const keys = Object.keys(variants);
    if (keys.length === 0) return null;
    const lang = locale.toLowerCase().split(/[-_]/)[0];
    const matchKey = keys.find((key) => key.toLowerCase().split(/[-_]/)[0] === lang) ?? keys[0];
    return variants[matchKey];
  }, [satisfactionForm, locale]);

  const displayRating = hoveredReview ?? selectedReview;

  // No satisfaction form configured (or no usable variant): render nothing.
  if (!satisfactionForm || !variant) return null;

  const ratingType = satisfactionForm.rating_type;
  const isFivePointRating = ratingType === RatingType.Smiley || ratingType === RatingType.Stars;
  const hasSelectedFivePointLabel =
    isFivePointRating && displayRating !== undefined && displayRating >= 1 && displayRating <= 5;

  // Step 1: store the rating as soon as the user picks one (preview mode never persists).
  const handleSelectReview = (value: number) => {
    setSelectedReview(value);
    if (isPreviewMode) return;
    debouncedSubmitRating(value);
  };

  // Step 2: store the written feedback (with the rating) when the user submits it.
  // Only runs once the form validates, so feedbackText is non-empty and already trimmed.
  const handleSubmitFeedback = ({ feedbackText }: FeedbackFormValues) => {
    reset({ feedbackText: "" });
    setIsDialogOpen(false);
    setIsReviewTextSent(true);
    if (isPreviewMode || selectedReview === undefined) return;
    // This call already carries the rating, so drop any pending debounced rating submit.
    debouncedSubmitRating.cancel();
    void onSubmitSurvey?.({
      ratingType,
      ratingValue: selectedReview,
      feedbackText,
    });
    // The cancelled rating submit will not redirect any more: do it here instead.
    redirectOnBadRating(selectedReview);
  };

  const reveal = getRevealProps({
    motionEnabled,
    timing: MESSAGE_REVEAL_TIMING,
    offsetY: MESSAGE_REVEAL_OFFSET_Y,
    withExit: true,
  });

  return (
    <>
      {/* Tied to the card, not rendered beside it: dismissing while the feedback dialog is
          open has to take the dialog with it. */}
      {isVisible && (
        <Dialog open={isDialogOpen} onOpenChange={handleDialogOpenChange}>
          <DialogContent
            id="message-review-dialog"
            className={cn(
              "h-(--message-review-dialog-height) w-(--message-review-dialog-width) max-w-(--message-review-dialog-max-width) flex flex-col p-(--message-review-dialog-padding) rounded-(--message-review-dialog-border-radius) max-h-[95vh] bg-(--message-review-dialog-background)/(--message-review-dialog-background-opacity) gap-6 overflow-y-auto overscroll-none",
              dialogPortalContainer && "max-h-[92%]"
            )}
            portalContainer={dialogPortalContainer}
            overlayProps={{
              className:
                "bg-(--message-review-dialog-overlay-background)/(--message-review-dialog-overlay-background-opacity)",
            }}
          >
            <DialogHeader id="message-review-dialog-header">
              <DialogTitle
                id="message-review-dialog-title"
                className={cn(
                  "text-(--message-review-dialog-title-color)/(--message-review-dialog-title-color-opacity) text-(length:--message-review-dialog-title-font-size)",
                  "font-(--message-review-dialog-title-font-weight)! font-(family-name:--message-review-dialog-title-font-family)",
                  "leading-(--message-review-dialog-title-line-height)",
                  "text-center"
                )}
              >
                {variant.text_feedback_title}
              </DialogTitle>
              <DialogDescription
                id="message-review-dialog-description"
                className={cn(
                  "text-(--message-review-dialog-label-color)/(--message-review-dialog-label-color-opacity)",
                  "text-(length:--message-review-dialog-label-font-size)",
                  "font-(--message-review-dialog-label-font-weight)!",
                  "font-(family-name:--message-review-dialog-label-font-family)",
                  "text-center text-balance"
                )}
              >
                {variant.text_feedback_label}
              </DialogDescription>
            </DialogHeader>

            <form className="contents" onSubmit={handleFeedbackFormSubmit(handleSubmitFeedback)}>
              <div id="message-review-dialog-content" className="flex flex-col gap-6 flex-1">
                <Controller
                  control={control}
                  name="feedbackText"
                  render={({ field, fieldState }) => (
                    <Field data-invalid={fieldState.invalid}>
                      <Textarea
                        {...field}
                        id="message-review-dialog-textarea"
                        aria-labelledby="message-review-dialog-description"
                        aria-invalid={fieldState.invalid}
                        className={cn(
                          "w-full",
                          "min-h-(--message-review-textarea-min-height)",
                          "rounded-(--message-review-textarea-border-radius)",
                          "border-(--message-review-textarea-border-color)/(--message-review-textarea-border-opacity)",
                          "bg-(--message-review-textarea-background)/(--message-review-textarea-background-opacity)",
                          "text-(--message-review-textarea-foreground)/(--message-review-textarea-foreground-opacity)",
                          "placeholder:text-(--message-review-textarea-placeholder-color)/(--message-review-textarea-placeholder-color-opacity)"
                        )}
                        placeholder={variant.text_feedback_placeholder}
                      />

                      <Label htmlFor="message-review-dialog-textarea" className="sr-only">
                        {variant.text_feedback_label}
                      </Label>

                      {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
                    </Field>
                  )}
                />
              </div>

              <DialogFooter
                id="message-review-dialog-footer"
                className="flex flex-row justify-end gap-2"
              >
                <Button
                  id="message-review-dialog-submit-button"
                  type="submit"
                  className={cn(
                    "bg-(--message-review-button-background)/(--message-review-button-background-opacity)",
                    "hover:bg-(--message-review-button-hover-background)/(--message-review-button-hover-background-opacity)",
                    "text-(--message-review-button-foreground)/(--message-review-button-foreground-opacity)",
                    "hover:text-(--message-review-button-hover-foreground)/(--message-review-button-hover-foreground-opacity)",
                    "border-(length:--message-review-button-border-width)",
                    "border-(--message-review-button-border-color)/(--message-review-button-border-opacity)",
                    "hover:border-(--message-review-button-hover-border-color)/(--message-review-button-hover-border-opacity)",
                    "rounded-(--message-review-button-border-radius)",
                    "h-(--message-review-button-height)",
                    "font-(--message-review-button-font-weight)!",
                    "text-(length:--message-review-button-font-size)",
                    "font-(family-name:--message-review-button-font-family)",
                    "[box-shadow:var(--message-review-button-box-shadow)]"
                  )}
                >
                  {variant.text_feedback_button}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      )}

      <AnimatePresence>
        {isVisible && (
          <ReviewPresence>
            <motion.section
              {...reveal}
              id="message-review"
              aria-labelledby="message-review-title"
              className={cn(
                "relative w-full flex flex-col justify-center items-center gap-2",
                "p-(--message-review-padding)",
                "rounded-(--message-review-border-radius)",
                "bg-(--message-review-background)/(--message-review-background-opacity)",
                "border-(--message-review-border-color)/(--message-review-border-opacity)",
                "border-(length:--message-review-border-width)",
                "max-w-(--message-review-max-width)"
              )}
            >
              <Button
                id="message-review-close-button"
                variant="ghost"
                size="icon"
                className="absolute top-(--message-review-padding) right-(--message-review-padding) group p-0 size-fit bg-transparent hover:bg-transparent"
                onClick={dismiss}
                aria-label={t("chat.message-review.close")}
              >
                <XIcon
                  aria-hidden="true"
                  className="size-4 text-muted-foreground group-hover:text-foreground"
                />
              </Button>

              <h2
                id="message-review-title"
                className={cn(
                  "text-(--message-review-title-color)/(--message-review-title-color-opacity)",
                  "text-(length:--message-review-title-font-size)",
                  "font-(--message-review-title-font-weight)!",
                  "font-(family-name:--message-review-title-font-family)",
                  "text-center"
                )}
              >
                {variant.rating_title}
              </h2>
              <p
                id="message-review-description"
                className={cn(
                  "text-(--message-review-label-color)/(--message-review-label-color-opacity)",
                  "text-(length:--message-review-label-font-size)",
                  "font-(--message-review-label-font-weight)!",
                  "font-(family-name:--message-review-label-font-family)",
                  "text-center text-balance"
                )}
              >
                {variant.rating_label}
              </p>

              <div
                id="message-review-content"
                className="flex flex-col gap-4 mt-3 w-full justify-center items-center"
              >
                {ratingType === RatingType.Smiley ? (
                  <div
                    id="message-review-rating-emojis"
                    role="group"
                    aria-labelledby="message-review-description"
                    className="flex flex-col items-center gap-2"
                  >
                    <div className="flex flex-row gap-4">
                      {FIVE_POINT_VALUES.map((value) => (
                        <button
                          key={value}
                          id={`message-review-rating-emoji-${value}`}
                          type="button"
                          aria-label={t(`chat.message-review.ratings.${value}`)}
                          aria-pressed={selectedReview === value}
                          className={cn(
                            "text-(length:--message-review-emoji-size) leading-[1] transition-all duration-300 ease-in-out",
                            selectedReview && selectedReview !== value && "grayscale-100 scale-90"
                          )}
                          onClick={() => handleSelectReview(value)}
                        >
                          <span aria-hidden="true">{SMILEY_EMOJIS[value]}</span>
                        </button>
                      ))}
                    </div>
                    {hasSelectedFivePointLabel ? (
                      <p
                        id="message-review-rating-selection"
                        aria-live="polite"
                        className="text-(length:--message-review-text-size) text-(--message-review-text-color)/(--message-review-text-color-opacity)"
                      >
                        {t(`chat.message-review.ratings.${displayRating}`)}
                      </p>
                    ) : null}
                  </div>
                ) : ratingType === RatingType.Stars ? (
                  <div
                    id="message-review-rating-stars"
                    role="group"
                    aria-labelledby="message-review-description"
                    className="flex flex-col items-center gap-2"
                    // Reset the hover when leaving the stars *and* their label, not the stars
                    // alone: the label appearing grows the card, which can re-scroll the
                    // conversation and slide the label under the cursor. Leaving on the stars
                    // row would then hide the label, scroll back and re-hover, in a loop.
                    onMouseLeave={() => setHoveredReview(undefined)}
                  >
                    <div className="flex flex-row gap-2">
                      {FIVE_POINT_VALUES.map((value) => {
                        const isFilled = displayRating !== undefined && value <= displayRating;

                        return (
                          <button
                            key={value}
                            id={`message-review-rating-star-${value}`}
                            type="button"
                            aria-label={t(`chat.message-review.ratings.${value}`)}
                            aria-pressed={selectedReview === value}
                            className={cn(
                              "transition-all duration-200 ease-in-out",
                              selectedReview &&
                                !hoveredReview &&
                                value > selectedReview &&
                                "opacity-40 scale-90"
                            )}
                            onClick={() => handleSelectReview(value)}
                            onMouseEnter={() => setHoveredReview(value)}
                          >
                            <StarIcon
                              aria-hidden="true"
                              className={cn(
                                "size-(--message-review-star-size) transition-colors duration-200",
                                isFilled
                                  ? "fill-amber-400 stroke-amber-400"
                                  : "fill-neutral-300 stroke-neutral-300"
                              )}
                            />
                          </button>
                        );
                      })}
                    </div>
                    {hasSelectedFivePointLabel ? (
                      <p
                        id="message-review-rating-selection"
                        aria-live="polite"
                        className="text-(length:--message-review-text-size) text-(--message-review-text-color)/(--message-review-text-color-opacity)"
                      >
                        {t(`chat.message-review.ratings.${displayRating}`)}
                      </p>
                    ) : null}
                  </div>
                ) : ratingType === RatingType.Numbers ? (
                  <div
                    id="message-review-rating-scale"
                    role="group"
                    aria-labelledby="message-review-description"
                    className="flex w-full self-stretch flex-col gap-2"
                  >
                    <div
                      id="message-review-rating-scale-grid"
                      className={cn(
                        "grid w-full grid-cols-11 overflow-hidden [&>button:not(:last-child)]:border-r-(length:--message-review-rating-grid-border-width)",
                        "border-(--message-review-rating-grid-border-color)/(--message-review-rating-grid-border-opacity)",
                        "border-(length:--message-review-rating-grid-border-width)",
                        "bg-(--message-review-background)/(--message-review-background-opacity)",
                        "rounded-(--message-review-rating-grid-border-radius)"
                      )}
                      onMouseLeave={() => setHoveredReview(undefined)}
                    >
                      {NUMBER_SCALE_VALUES.map((value) => {
                        const isActive = displayRating === value;

                        return (
                          <button
                            key={value}
                            id={`message-review-rating-scale-${value}`}
                            type="button"
                            aria-label={String(value)}
                            aria-pressed={selectedReview === value}
                            className={cn(
                              "aspect-square w-full min-h-0 flex items-center justify-center text-lg font-semibold leading-none tabular-nums transition-colors duration-200",
                              isActive
                                ? "bg-(--message-review-rating-active-background-color)/(--message-review-rating-active-background-color-opacity) text-(--message-review-rating-active-text-color)/(--message-review-rating-active-text-color-opacity)"
                                : "text-(--message-review-label-color)/60 hover:bg-muted"
                            )}
                            onClick={() => handleSelectReview(value)}
                            onMouseEnter={() => setHoveredReview(value)}
                          >
                            {value}
                          </button>
                        );
                      })}
                    </div>
                    <div
                      id="message-review-rating-scale-legend"
                      className="flex w-full justify-between text-(length:--message-review-text-size) text-(--message-review-text-color)/(--message-review-text-color-opacity)"
                      aria-hidden="true"
                    >
                      <span>{t("chat.message-review.scale-low")}</span>
                      <span>{t("chat.message-review.scale-high")}</span>
                    </div>
                  </div>
                ) : null}
              </div>

              {showFollowUp && (
                <div
                  id="message-review-follow-up"
                  className="flex flex-col gap-4 mt-2 w-full justify-center items-center"
                >
                  <p
                    id="message-review-follow-up-description"
                    className={cn(
                      "text-(--message-review-label-color)/(--message-review-label-color-opacity)",
                      "text-(length:--message-review-label-font-size)",
                      "font-(--message-review-label-font-weight)!",
                      "font-(family-name:--message-review-label-font-family)",
                      "text-center text-balance"
                    )}
                  >
                    {variant.thank_you_message}
                  </p>

                  <Button
                    id="message-review-feedback-button"
                    aria-describedby="message-review-follow-up-description"
                    className={cn(
                      "w-full",
                      "bg-(--message-review-button-background)/(--message-review-button-background-opacity)",
                      "hover:bg-(--message-review-button-hover-background)/(--message-review-button-hover-background-opacity)",
                      "text-(--message-review-button-foreground)/(--message-review-button-foreground-opacity)",
                      "hover:text-(--message-review-button-hover-foreground)/(--message-review-button-hover-foreground-opacity)",
                      "border-(length:--message-review-button-border-width)",
                      "border-(--message-review-button-border-color)/(--message-review-button-border-opacity)",
                      "hover:border-(--message-review-button-hover-border-color)/(--message-review-button-hover-border-opacity)",
                      "rounded-(--message-review-button-border-radius)",
                      "h-(--message-review-button-height)",
                      "font-(--message-review-button-font-weight)!",
                      "text-(length:--message-review-button-font-size)",
                      "font-(family-name:--message-review-button-font-family)",
                      "[box-shadow:var(--message-review-button-box-shadow)]"
                    )}
                    onClick={() => setIsDialogOpen(true)}
                  >
                    {variant.feedback_button}
                  </Button>
                </div>
              )}

              {showFeedbackThankYou && (
                <p
                  id="message-review-feedback-thank-you"
                  className={cn(
                    "mt-2 w-full text-center text-balance",
                    "text-(--message-review-label-color)/(--message-review-label-color-opacity)",
                    "text-(length:--message-review-label-font-size)",
                    "font-(--message-review-label-font-weight)!",
                    "font-(family-name:--message-review-label-font-family)"
                  )}
                >
                  {variant.feedback_thank_you_message}
                </p>
              )}
            </motion.section>
          </ReviewPresence>
        )}
      </AnimatePresence>
    </>
  );
};
